import { z } from 'zod';
import { outcomeSchema } from './outcome.js';
/** Status einer Teilnahme (siehe ARCHITECTURE.md 12.7.1). */
export const competitionStatusSchema = z.enum(['open', 'results_entered', 'closed']);
/** Zulässige Medien einer Teilnahme. */
export const mediumSchema = z.enum(['digital', 'print']);
/** FIAP-Nummer `JJJJ/NNN`. */
const fiapNumberSchema = z
    .string()
    .trim()
    .regex(/^\d{4}\/\d{3}$/, 'invalid_format');
/** Zähler der (effektiven) Ergebnisse einer Teilnahme. */
export const competitionStatsSchema = z.object({
    total: z.number().int(),
    open: z.number().int(),
    rejected: z.number().int(),
    acceptance: z.number().int(),
    honorableMention: z.number().int(),
    medal: z.number().int(),
});
/** Teilnahme an einem Wettbewerb, wie die API sie ausgibt. */
export const competitionSchema = z.object({
    id: z.number().int(),
    salonId: z.number().int().nullable(),
    fiapNumber: z.string().nullable(),
    name: z.string(),
    country: z.string().nullable(),
    medium: mediumSchema,
    categories: z.array(z.string()),
    status: competitionStatusSchema,
    closedAt: z.string().nullable(),
    createdAt: z.string(),
    /** Einsendeschluss des zugehörigen Salons (Datum), falls bekannt. */
    closingDate: z.string().nullable(),
    stats: competitionStatsSchema,
});
/** Abfrageparameter der Teilnahmeliste. */
export const competitionListQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    status: competitionStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Antwort der Teilnahmeliste. */
export const competitionListSchema = z.object({
    items: z.array(competitionSchema),
    total: z.number().int(),
});
/** Anlegen einer Teilnahme: aus einem Salon des Katalogs oder manuell (Name erforderlich). */
export const competitionCreateSchema = z
    .object({
    salonId: z.number().int().positive().optional(),
    name: z.string().trim().min(1, 'required').max(200, 'too_long').optional(),
    fiapNumber: fiapNumberSchema.nullable().optional(),
    country: z.string().trim().max(100).nullable().optional(),
    medium: mediumSchema.optional(),
    categories: z.array(z.string().trim().min(1).max(100)).max(30).optional(),
})
    .refine((value) => value.salonId !== undefined || value.name !== undefined, {
    message: 'required',
    path: ['name'],
});
/** Ändern einer Teilnahme (nur angegebene Felder). */
export const competitionPatchSchema = z
    .object({
    name: z.string().trim().min(1, 'required').max(200, 'too_long'),
    fiapNumber: fiapNumberSchema.nullable(),
    country: z.string().trim().max(100).nullable(),
    medium: mediumSchema,
    categories: z.array(z.string().trim().min(1).max(100)).max(30),
})
    .partial();
/** Einreichung eines Bildes in einer Teilnahme. */
export const submissionSchema = z.object({
    id: z.number().int(),
    imageId: z.number().int(),
    imageTitle: z.string(),
    hasThumb: z.boolean(),
    category: z.string().nullable(),
    /** Gespeichertes Ergebnis. */
    outcome: outcomeSchema,
    /** Effektives Ergebnis (offen in abgeschlossener Teilnahme = abgelehnt). */
    effectiveOutcome: outcomeSchema,
    awardNote: z.string().nullable(),
    decidedAt: z.string().nullable(),
    /** Aus Altdaten abgeleitet und noch nicht bestätigt. */
    legacyInferred: z.boolean(),
});
/** Teilnahme mit allen Einreichungen. */
export const competitionDetailSchema = competitionSchema.extend({
    submissions: z.array(submissionSchema),
});
/** Bestehende Bilder zu einer Teilnahme hinzufügen. */
export const addSubmissionsBodySchema = z.object({
    imageIds: z.array(z.number().int().positive()).min(1).max(500),
    category: z.string().trim().max(100).nullable().optional(),
});
/** Antwort: neu eingereichte und bereits vorhandene Bilder. */
export const addSubmissionsResponseSchema = z.object({
    added: z.number().int(),
    skipped: z.number().int(),
});
/** Ergebnis einer einzelnen Einreichung, gesetzt in der Wettbewerbsansicht. */
export const outcomeChangeSchema = z.object({
    submissionId: z.number().int().positive(),
    outcome: outcomeSchema,
    awardNote: z.string().trim().max(100).nullable().optional(),
});
/** Ergebnisse mehrerer Einreichungen auf einmal setzen. */
export const outcomesBodySchema = z.object({
    changes: z.array(outcomeChangeSchema).max(1000).default([]),
    /** Alle danach noch offenen Einreichungen als abgelehnt markieren. */
    rejectRemaining: z.boolean().default(false),
    /** Entscheidungsdatum für neu entschiedene Einreichungen (Standard: heute). */
    decidedAt: z.iso.date().optional(),
});
/** Antwort: Anzahl geänderter Einreichungen und neuer Status. */
export const outcomesResponseSchema = z.object({
    updated: z.number().int(),
    status: competitionStatusSchema,
});
/** Ändern einer einzelnen Einreichung. */
export const submissionPatchSchema = z
    .object({
    category: z.string().trim().max(100).nullable(),
    outcome: outcomeSchema,
    awardNote: z.string().trim().max(100).nullable(),
})
    .partial();
//# sourceMappingURL=competitions.js.map